import React, {useEffect, useRef, useState, useCallback} from 'react'
import {useIntl, defineMessages} from 'react-intl'
import PropTypes from 'prop-types'
import {nanoid} from 'nanoid'

import {
    Box,
    Button,
    Heading,
    Stack,
    HStack,
    Text,
    Flex,
    Alert,
    IconButton,

    // Hooks
    useTheme,
    useBreakpointValue
} from '@salesforce/retail-react-app/app/components/shared/ui'

/* Hooks */
import {useHistory, useLocation} from 'react-router-dom'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useShopperCustomersMutation} from '@salesforce/commerce-sdk-react'
import {useCustomToast} from '../../contexts/custom-toast-context'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {SITE_COUNTRY_MAP} from '../../constants'
import {useBrand} from '../../contexts/brand-context'

/* Icons */
import {LeftArrow, EditPencil, InfoCircleBlack} from '../../components/icons'

/* Forms */
import {useForm} from 'react-hook-form'
import AddressBookFields from '../../components/forms/address-book-fields'

/* Components */
import LoadingSpinner from '@salesforce/retail-react-app/app/components/loading-spinner'
import {sendViewErrorEvent} from '../../utils/gtm-utils'
import {usePhoneValidation, useAddressValidation} from '../../integrations/melissa/hooks/use-melissa'
import { InfoCard, useControlledDisclosure } from '../../components/info-card'
import StandardDrawer from '../../components/standard-drawer'
import AddressValidationDrawer from '../../pages/checkout/partials/address-validation-drawer'
/* Constants and static resources */
const staticResources = defineMessages({
    addressHeading: {
        id: 'account.address_book.address_heading',
        defaultMessage: 'Address Book'
    },
    newAddressHeading: {
        id: 'account.address_book.new_address_heading',
        defaultMessage: 'Add Address'
    },
    editAddressHeading: {
        id: 'account.address_book.edit_address_heading',
        defaultMessage: 'Update Address'
    },
    addAddressLabel: {
        id: 'account.address_book.add_address',
        defaultMessage: 'Add Address'
    },
    saveAddressLabel: {
        id: 'account.address_book.save_address',
        defaultMessage: 'Save Address'
    },
    updatedAddressLabel: {
        id: 'account.address_book.update_address',
        defaultMessage: 'Update Address'
    },
    deleteAddressLabel: {
        id: 'account.address_book.delete_address',
        defaultMessage: 'Delete this address'
    },
    noAddressTitle: {
        id: 'account.address_book.no_address_title',
        defaultMessage: 'No Saved Addresses'
    },
    noAddressSubTitleGarage: {
        id: 'account.address_book.no_address_subtitle.garage',
        defaultMessage: 'Click below to add an address.'
    },
    noAddressSubTitleDynamite: {
        id: 'account.address_book.no_address_subtitle.dynamite',
        defaultMessage: 'Click below to add an address.'
    },
    successMessage: {
        id: 'account.address_book.success_message',
        defaultMessage: 'The changes have been saved'
    },
    errorMessageGarage: {
        id: 'account.address_book.error_message.garage',
        defaultMessage: 'Sorry, something went wrong. Please try again.'
    },
    errorMessageDynamite: {
        id: 'account.address_book.error_message.dynamite',
        defaultMessage: 'Sorry, something went wrong. Please try again.'
    },
    preferred: {
        id: 'account.address_book.default_address',
        defaultMessage: 'Default address'
    },
    ukCountryName: {
        id: 'account.address_book.uk_country_name',
        defaultMessage: 'United Kingdom'
    },
    editAddress: {
        id: 'account.address_book.edit_address',
        defaultMessage: 'Edit'
    },
    removeAddress: {
        id: 'account.address_book.remove_address',
        defaultMessage: 'Remove'
    }
})

const createAddressFormResetObject = (defaultCountry) => ({
    addressTitle: '',
    address1: '',
    apartment: '',
    city: '',
    countryCode: defaultCountry,
    firstName: '',
    lastName: '',
    phone: '',
    postalCode: '',
    stateCode: '',
    preferred: false,
    email: ''
})

const MANDATORY_FIELDS = [
    'firstName',
    'lastName',
    'address1',
    'city',
    'postalCode',
    'countryCode',
    'phone'
]

/* Helper functions */
const getAddressModeFromPath = (search) => {
    const params = new URLSearchParams(search)
    return params.get('mode') || null
}

const getDefaultMode = (addresses) => {
    return addresses?.length > 0 ? 'savedaddress' : 'noaddress'
}

const HeadingAddressBook = () => {
    const intl = useIntl()

    return (
        <Text alignItems="center" as="h1" textStyle="webH1" tabIndex="0" gap={6}>
            {intl.formatMessage(staticResources.addressHeading)}
        </Text>
    )
}

HeadingAddressBook.propTypes = {
    addressMode: PropTypes.string,
    backFromAddAddress: PropTypes.func,
    backFromEditAddress: PropTypes.func
}

/**
 * AddressCard is a presentational React component that displays a single address entry within an address book.
 * It renders the address details, including the recipient's name, address lines, city, postal code, and country.
 * If the address is marked as preferred, a label is shown. An edit icon is provided to trigger address editing.
 * The component uses theming for styling and internationalization for localized labels.
 *
 * Props:
 * @param {Object} address - The address object containing details to display (e.g., firstName, lastName, address1, city, postalCode, countryCode, title, preferred, c_apartment).
 * @param {Function} handleEditAddress - Callback function invoked when the edit icon is clicked, receiving the address object as an argument.
 * @param {boolean} isLastAddress - Indicates if this is the last address in the list, affecting the display of the bottom border.
 *
 * The component leverages theme-based styles and internationalization hooks for consistent UI and localization.
 */
const AddressCard = ({address, handleEditAddress, isLastAddress}) => {
    /* Styling */
    const theme = useTheme()
    const {baseStyle, addressCard} = theme.components.AddressBook

    /* Utility Hooks */
    const intl = useIntl()

    return (
        <Box
            borderBottom={!isLastAddress ? '1px solid' : '0'}
            paddingBottom={!isLastAddress ? 8 : 0}
            {...addressCard.container}
        >
            <Box {...baseStyle.centerContent}>
                {address?.preferred && (
                    <Text {...addressCard.preferred}>
                        {intl.formatMessage(staticResources.preferred)}
                    </Text>
                )}
                <Box {...addressCard.wrapper}>
                    {address?.title && (
                        <Text {...addressCard.title} data-private>
                            {address?.title}
                        </Text>
                    )}
                    <Text {...addressCard.address}>
                        {address.firstName} {address.lastName}
                    </Text>
                    <Text {...addressCard.address} data-private>
                        {address?.address1}
                        {address?.c_apartment ? `, ${address.c_apartment}` : ''}
                    </Text>
                    <Text {...addressCard.address} data-private>
                        {address?.city}, {address?.postalCode},{' '}
                        {address?.countryCode === 'GB'
                            ? intl.formatMessage(staticResources.ukCountryName)
                            : address?.countryCode}
                    </Text>
                    <IconButton
                        variant="unstyled"
                        h={'auto'}
                        minW={'auto'}
                        onClick={() => handleEditAddress(address)}
                        {...addressCard.editIcon}
                        icon={<EditPencil />}
                        aria-label="Edit address"
                    />
                </Box>
            </Box>
        </Box>
    )
}

AddressCard.propTypes = {
    address: PropTypes.object,
    handleEditAddress: PropTypes.func,
    isLastAddress: PropTypes.bool
}


const AddressForm = ({form, submitForm, addressMode, globalErrorRef, watchedValues}) => {
    const theme = useTheme()
    const {baseStyle, formStyle} = theme.components.AddressBook
    const intl = useIntl()
    const isFormValid = MANDATORY_FIELDS.every((field) => {
        const value = watchedValues[field]
        return value && value.toString().trim() !== ''
    })
    return (<form
                onSubmit={form.handleSubmit(submitForm)}
                style={{display: 'flex', flexDirection: 'column'}}
            >
                <Box
                    {...formStyle.container}

                    {...baseStyle.centerContent}
                >
                    <Stack spacing={4}>
                        {form.formState.errors?.global && (
                            <Box ref={globalErrorRef} {...formStyle.errorContainer}>
                                <Alert variant={'custom'} status="error">
                                    <Text>{form.formState.errors.global.message}</Text>
                                </Alert>
                            </Box>
                        )}
                        <AddressBookFields form={form} />
                    </Stack>
                    <Box>
                        <Button
                            type="submit"
                            title="Submit"
                            isDisabled={addressMode === 'addaddress' ? false : !isFormValid}
                            {...baseStyle.cta}
                            {...formStyle.submitCta}
                        >
                            {addressMode === 'addaddress'
                                ? intl.formatMessage(staticResources.addAddressLabel)
                                : intl.formatMessage(staticResources.updatedAddressLabel)}
                        </Button>
                    </Box>
                </Box>
            </form>);
}

/**
 * AddressBook is a React functional component that provides a comprehensive user interface for managing customer addresses within an account page.
 *
 * This component allows users to view, add, edit, and remove addresses associated with their account. It leverages several hooks for state management, form handling, API interactions, and responsive design. The component dynamically renders different UI states based on the current address mode (e.g., viewing saved addresses, adding a new address, editing an existing address, or displaying an empty state when no addresses are present).
 *
 * Key Features:
 * - Displays a list of saved addresses with options to edit or delete each address.
 * - Provides forms for adding and editing addresses, with validation for mandatory fields.
 * - Handles navigation between different address modes using URL parameters and browser history.
 * - Shows loading spinners during asynchronous operations and displays success or error toasts based on API responses.
 * - Responsive design adapts the layout for mobile and desktop views.
 *
 * Dependencies:
 * - Uses custom hooks such as `useCurrentCustomer`, `useShopperCustomersMutation`, `useCustomToast`, and `useForm` for data fetching, mutations, notifications, and form state management.
 * - Integrates with theme styling and internationalization for consistent UI and localization.
 *
 * Returns:
 *   A JSX structure that conditionally renders address management forms, address cards, and appropriate UI elements based on the user's interaction and current state.
 */

/**
 * Filters address suggestions to prioritize those matching the entered apartment.
 * If user entered an apartment number, this function ensures only relevant apartments are shown.
 * 
 * @param {Array} suggestions - Array of address suggestions from validation API
 * @param {String} enteredApartment - The apartment number the user entered (optional)
 * @returns {Array} Filtered suggestions, or original if no apartment filter needed
 */
const filterSuggestionsByApartment = (suggestions, enteredApartment) => {
    if (!suggestions || suggestions.length === 0 || !enteredApartment) {
        return suggestions
    }

    // Normalize apartment string for comparison (remove spaces, dashes, convert to lowercase)
    const normalize = (str) => {
        if (!str) return ''
        return str.replace(/[\s\-]/g, '').toLowerCase()
    }

    const normalizedEntered = normalize(enteredApartment)

    // Separate suggestions into those with and without apartment numbers
    const noApartmentSuggestions = suggestions.filter((s) => !s.address2)
    const withApartmentSuggestions = suggestions.filter((s) => !!s.address2)

    if (withApartmentSuggestions.length === 0) {
        return suggestions
    }

    // Try to find exact or partial matches
    const exactMatches = withApartmentSuggestions.filter(
        (s) => normalize(s.address2) === normalizedEntered
    )

    if (exactMatches.length > 0) {
        // If we found exact matches, return those plus any non-apartment suggestions
        return [...noApartmentSuggestions, ...exactMatches]
    }

    // Look for partial matches (entered apartment is contained in suggestion)
    const partialMatches = withApartmentSuggestions.filter((s) =>
        normalize(s.address2).includes(normalizedEntered) ||
        normalizedEntered.includes(normalize(s.address2))
    )

    if (partialMatches.length > 0) {
        // Return partial matches plus non-apartment suggestions
        return [...noApartmentSuggestions, ...partialMatches]
    }

    // If no matches found, return all original suggestions
    return suggestions
}

const AddressBook = () => {
    /* Styling */
    const theme = useTheme()
    const {baseStyle} = theme.components.AddressBook

    /* Utility Hooks */
    const intl = useIntl()
    const isMobile = useBreakpointValue({base: true, md: false})

    const {showCustomToast} = useCustomToast()
    const {site} = useMultiSite()
    const defaultCountry = SITE_COUNTRY_MAP[site?.id] || 'CA'
    const form = useForm({
        mode: 'onChange',
        defaultValues: createAddressFormResetObject(defaultCountry)
    })
    const {data: customer, refetch, isLoading: isCustomerLoading} = useCurrentCustomer()
    const history = useHistory()
    const location = useLocation()

    /* States */
    const [addressMode, setAddressMode] = useState(null)
    const [selectedAddressId, setSelectedAddressId] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const lastSubmittedValuesRef = useRef(null)
    const globalErrorRef = useRef(null)
    const [skipAddressValidation, setSkipAddressValidation] = useState(false)
    const [pendingAVSSubmit, setPendingAVSSubmit] = useState(false)
    const [isAVSDrawerOpen, setIsAVSDrawerOpen] = useState(false)
    const [addressSuggestions, setAddressSuggestions] = useState([])
    const [isZero, setIsZero] = useState(false)
    const [isSingle, setIsSingle] = useState(true)
    const [isPoxBoxOrMilitary, setIsPoxBoxOrMilitary] = useState(false)
    const [selectedValidatedAddress, setSelectedValidatedAddress] = useState('')
        
    /* API Hooks */
    const addCustomerAddress = useShopperCustomersMutation('createCustomerAddress')
    const updateSavedAddress = useShopperCustomersMutation('updateCustomerAddress')
    const removeCustomerAddress = useShopperCustomersMutation('removeCustomerAddress')
    const {fetchPhoneValidation} = usePhoneValidation()
    const {validateAddress} = useAddressValidation()

    /* Others */
    const {addresses, customerId} = customer
    const watchedValues = form.watch()
    const {isGarage} = useBrand()
    const errorMessage = isGarage
        ? staticResources.errorMessageGarage
        : staticResources.errorMessageDynamite
    
    

    /* Navigation helper functions */
    const navigateToMode = (mode) => {
        const currentPath = location.pathname

        if (mode === 'savedaddress' || mode === 'noaddress') {
            history.push(currentPath)
        } else {
            const params = new URLSearchParams()
            params.set('mode', mode)
            history.push(`${currentPath}?${params.toString()}`)
        }
    }

    useEffect(() => {
        const urlMode = getAddressModeFromPath(location.search)

        if (urlMode) {
            setAddressMode(urlMode)
        } else {
            const defaultMode = getDefaultMode(addresses)
            setAddressMode(defaultMode)
        }
    }, [addresses, location.search])

    useEffect(() => {
        const handlePopState = () => {
            const urlMode = getAddressModeFromPath(location.search) || getDefaultMode(addresses)

            setAddressMode(urlMode)

            if (urlMode === 'savedaddress' || urlMode === 'noaddress') {
                form.reset()
                setSelectedAddressId(false)
            }
        }

        window.addEventListener('popstate', handlePopState)
        return () => window.removeEventListener('popstate', handlePopState)
    }, [addresses, location.search, form])

    const globalError = form.formState.errors?.global

    useEffect(() => {
        if (!globalError || !lastSubmittedValuesRef.current) {
            return
        }

        const hasChangedSinceSubmit = Object.keys(lastSubmittedValuesRef.current).some(
            (key) => lastSubmittedValuesRef.current[key] !== watchedValues[key]
        )

        if (hasChangedSinceSubmit) {
            form.clearErrors('global')
        }
    }, [globalError, watchedValues, form])

    useEffect(() => {
        if (!globalError || !globalErrorRef.current) {
            return
        }

        globalErrorRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
        })
    }, [globalError])

    const closeEditDrawer = useCallback(() => {
        form.reset(createAddressFormResetObject(defaultCountry))
        setIsLoading(false); 
        navigateToMode(getDefaultMode(addresses)); 
        setAddressMode(getDefaultMode(addresses));
    }, [])

    const editAddressDrawer = useControlledDisclosure({
        onClosing: closeEditDrawer
    })
    const submitForm = async (address) => {
        setIsLoading(true)
        lastSubmittedValuesRef.current = form.getValues()

        const {
            addressTitle,
            firstName,
            lastName,
            address1,
            apartment,
            city,
            postalCode,
            countryCode,
            phone,
            stateCode,
            preferred
        } = address
        try {
            let addressValidated = false
            if(!skipAddressValidation) {
                const resultAddressValidation = await validateAddress({address, shouldFetch: true})
                if (resultAddressValidation) {
                    // If entered address is valid 
                    let suggestedAddresses = resultAddressValidation?.suggestedAddresses || []
                    
                    // Filter suggestions by entered apartment if provided
                    if (apartment) {
                        suggestedAddresses = filterSuggestionsByApartment(suggestedAddresses, apartment)
                    }
                    
                    if (resultAddressValidation?.valid && suggestedAddresses?.length === 0) { 
                        addressValidated = true
                    } else {
                        setIsAVSDrawerOpen(true)
                        setAddressSuggestions(suggestedAddresses)
                        setIsSingle(suggestedAddresses.length === 1)
                        setIsZero(suggestedAddresses.length === 0)
                        setIsPoxBoxOrMilitary(resultAddressValidation?.inputAddress?.isPoxBoxOrMilitary)
                        setSelectedValidatedAddress(suggestedAddresses.length === 1 ? 'suggested' : '')
                        setIsLoading(false)
                        return
                    }

                }
            } else {
                addressValidated = true
            }
            
            const normalizedPhone = (phone || '').replace(/[^\d]/g, '')

            const result = await fetchPhoneValidation({
                phoneNumber: normalizedPhone,
                countryCode,
                shouldFetch: true
            })

            const isValidPhone = result?.phoneValidationResult?.isValidPhone ?? true

            if (!isValidPhone) {
                form.setError('phone', {
                    type: 'manual',
                    message: intl.formatMessage({
                        defaultMessage: 'Invalid phone number',
                        id: 'address.phone.invalid'
                    })
                })

                setIsLoading(false)
                return
            }

            form.clearErrors('phone')

            let data = null
            if(addressValidated) { 
                if (selectedAddressId) {
                    const body = {
                        addressId: selectedAddressId,
                        title: addressTitle,
                        firstName,
                        lastName,
                        address1,
                        c_apartment: apartment,
                        city,
                        postalCode,
                        countryCode,
                        phone,
                        stateCode,
                        c_email: customer.email,
                        preferred,
                        c_isAddressBookCall: true
                    }
                    data = await updateSavedAddress.mutateAsync({
                        body,
                        parameters: {
                            customerId,
                            addressName: selectedAddressId
                        }
                    })
                    refetch()
                } else {
                    // Automatically set first address as default
                    const isFirstAddress = !addresses || addresses.length === 0
                    const body = {
                        addressId: nanoid(),
                        title: addressTitle,
                        firstName,
                        lastName,
                        address1,
                        c_apartment: apartment,
                        city,
                        postalCode,
                        countryCode,
                        phone,
                        stateCode,
                        c_email: customer.email,
                        preferred: isFirstAddress ? true : preferred,
                        c_isAddressBookCall: true
                    }
                    data = await addCustomerAddress.mutateAsync({
                        body,
                        parameters: {customerId: customer.customerId}
                    })
                    refetch()
                }

                if (data) {
                    showCustomToast({
                        message: intl.formatMessage(staticResources.successMessage)
                    })

                    navigateToMode('savedaddress')
                    setAddressMode('savedaddress')
                    setSelectedAddressId(false)
                    form.reset()
                }

                setIsLoading(false)
                setSkipAddressValidation(false)
                editAddressDrawer.close()
            }
        } catch {
            form.setError('global', {
                type: 'manual',
                message: errorMessage
            })
            setIsLoading(false)
            // GTM Modification
            sendViewErrorEvent(errorMessage)
        }
    }

    // Re-submit after AVS confirm so skipAddressValidation is applied (same pattern as checkout).
    useEffect(() => {
        if (pendingAVSSubmit) {
            setPendingAVSSubmit(false)
            form.handleSubmit(submitForm)()
        }
    }, [pendingAVSSubmit])

    const handleAddAddress = () => {
        setSkipAddressValidation(false)
        form.reset(createAddressFormResetObject(defaultCountry))
        navigateToMode('addaddress')
        setAddressMode('addaddress')
        setSelectedAddressId(false)
    }

    const handleEditAddress = (address) => {
        setSkipAddressValidation(false)
        form.reset({
            addressTitle: address.title || '',
            address1: address.address1 || '',
            apartment: address.c_apartment || '',
            city: address.city || '',
            countryCode: address.countryCode || defaultCountry,
            firstName: address.firstName || '',
            lastName: address.lastName || '',
            phone: address.phone || '',
            postalCode: address.postalCode || '',
            stateCode: address.stateCode || '',
            preferred: address.preferred || false,
            email: address.c_email || ''
        })
        navigateToMode('editaddress')
        setAddressMode('editaddress')
        setSelectedAddressId(address?.addressId)
    }

    const handleRemoveAddress = async (addressId) => {
        if (!addressId) {
            form.setError('global', {
                type: 'manual',
                message: errorMessage
            })
            // GTM Modification
            sendViewErrorEvent(errorMessage)
            return
        }

        setIsLoading(true)

        try {
            await removeCustomerAddress.mutateAsync(
                {
                    parameters: {
                        customerId,
                        addressName: addressId
                    }
                },
                {
                    onSuccess: () => {
                        navigateToMode('savedaddress')
                        setAddressMode('savedaddress')
                        setSelectedAddressId(false)
                        form.reset()
                        showCustomToast({
                            message: intl.formatMessage(staticResources.successMessage)
                        })
                        setIsLoading(false)
                    },
                    onError: () => {
                        form.setError('global', {
                            type: 'manual',
                            message: errorMessage
                        })
                        setIsLoading(false)
                        // GTM Modification
                        sendViewErrorEvent(errorMessage)
                    }
                }
            )
            {(addressMode === 'editaddress') && editAddressDrawer.close()}
        } catch {
            form.setError('global', {
                type: 'manual',
                message: intl.formatMessage(errorMessage)
            })
            // GTM Modification
            sendViewErrorEvent(errorMessage)
            setIsLoading(false)
        }
    }

    const handleAddDrawerClose = () => {
        setIsLoading(false); 
        form.reset()
        navigateToMode(getDefaultMode(addresses))
        setAddressMode(getDefaultMode(addresses))
    }
    
    const handleAVSDrawerSubmitOrClose = async (confirmed, selectedData) => {
        if (confirmed && !isZero) {
            // Preserve apartment value before updating other fields
            const currentApartment = form.getValues('apartment')

            if (!isSingle && selectedValidatedAddress) {
                // If selectedData is provided (from GeoMismatchDrawer), use it directly
                if (selectedData && selectedData.suggestion) {
                    const s = selectedData.suggestion
                    form.setValue('address1', s.address1)
                    form.setValue('address2', s.address2)
                    form.setValue('city', s.city)
                    form.setValue('stateCode', s.stateCode)
                    form.setValue('postalCode', s.postalCode)
                    if (s.address2) {
                        form.setValue('apartment', s.address2)
                    } else if (currentApartment) {
                        form.setValue('apartment', currentApartment)
                    }
                } else {
                    // Fallback: try to find by matching string
                    const selectedSuggestion = addressSuggestions.find((suggestion) => {
                        const formattedSuggestion = [
                            suggestion.address1,
                            suggestion.address2,
                            suggestion.city,
                            suggestion.stateCode,
                            suggestion.postalCode
                        ]
                            .filter(Boolean)
                            .join(', ')

                        return formattedSuggestion === selectedValidatedAddress
                    })
                    if (selectedSuggestion) {
                        form.setValue('address1', selectedSuggestion.address1)
                        form.setValue('address2', selectedSuggestion.address2)
                        form.setValue('city', selectedSuggestion.city)
                        form.setValue('stateCode', selectedSuggestion.stateCode)
                        form.setValue('postalCode', selectedSuggestion.postalCode)
                        // Preserve apartment if not in suggestion
                        if (selectedSuggestion.address2) {
                            form.setValue('apartment', selectedSuggestion.address2)
                        } else if (currentApartment) {
                            form.setValue('apartment', currentApartment)
                        }
                    }
                }
            } else if (isSingle && addressSuggestions && addressSuggestions.length > 0) {
                if (selectedValidatedAddress === 'suggested') {
                    form.setValue('address1', addressSuggestions[0].address1)
                    form.setValue('address2', addressSuggestions[0].address2)
                    form.setValue('city', addressSuggestions[0].city)
                    form.setValue('stateCode', addressSuggestions[0].stateCode)
                    form.setValue('postalCode', addressSuggestions[0].postalCode)
                    // Preserve apartment if not in suggestion
                    if (addressSuggestions[0].address2) {
                        form.setValue('apartment', addressSuggestions[0].address2)
                    } else if (currentApartment) {
                        form.setValue('apartment', currentApartment)
                    }
                }
            }

            setSkipAddressValidation(true)
            setPendingAVSSubmit(true)
        } else {
            setIsLoading(false)
        }
        setIsAVSDrawerOpen(false)
    }

    return (
        <Stack data-testid="account-address-book" {...baseStyle.container}>
            <HeadingAddressBook key="account-address-heading" />
            {(addressMode === 'noaddress' || (addressMode === 'addaddress' && (!addresses || addresses.length === 0))) && (
                <Flex {...baseStyle.addressWrapper} {...baseStyle.centerContent}>
                    <Box>
                        <HStack {...baseStyle.noAddressTitle}>
                            <InfoCircleBlack width={17} height={17} />
                            <Text>{intl.formatMessage(staticResources.noAddressTitle)}</Text>
                        </HStack>
                        <Text {...baseStyle.noAddressSubTitle}>
                            {isGarage
                                ? intl.formatMessage(staticResources.noAddressSubTitleGarage)
                                : intl.formatMessage(staticResources.noAddressSubTitleDynamite)}
                        </Text>
                    </Box>
                    <Button
                        onClick={handleAddAddress}
                        {...baseStyle.cta}
                        {...baseStyle.addAddressCta}
                    >
                        {intl.formatMessage(staticResources.addAddressLabel)}
                    </Button>
                </Flex>
            )}
            {addressMode === 'addaddress' && (
                <StandardDrawer
                    data-testid={`addaddress-drawer`}
                    isOpen={addressMode === 'addaddress'}
                    onClose={handleAddDrawerClose}
                    ariaLabelledBy={intl.formatMessage(staticResources.newAddressHeading)}
                    headerTitle={intl.formatMessage(staticResources.newAddressHeading)}
                    headerTitleId={intl.formatMessage(staticResources.newAddressHeading)}
                >
                    <AddressForm
                        form={form}
                        submitForm={submitForm}
                        addressMode={addressMode}
                        globalErrorRef={globalErrorRef}
                        watchedValues={watchedValues}
                    />
                </StandardDrawer>
            )}
            {addresses && addresses.length && (
                <Flex {...baseStyle.addressWrapper}>
                    <Box>
                        {addresses?.map((address, index) => {
                            const addressSummary = (
                                <>
                                    <Text>
                                        {address.firstName} {address.lastName}
                                    </Text>
                                    <Text>
                                        {address?.address1}
                                        {address?.c_apartment
                                            ? `, ${address.c_apartment}`
                                            : ''}
                                        , {address?.city}, {address?.postalCode},{' '}
                                        {address?.countryCode === 'GB'
                                            ? intl.formatMessage(staticResources.ukCountryName)
                                            : address?.countryCode}
                                    </Text>
                                    <Text>{address.phone}</Text>
                                </>
                            )
                            const addressId = address?.addressId
                            const isEditingThisAddress =
                                editAddressDrawer.isOpen && selectedAddressId === addressId

                            return (
                                <InfoCard
                                    {...editAddressDrawer}
                                    id={addressId || `address-${index}`}
                                    title={address?.title}
                                    drawerTitle={intl.formatMessage(
                                        staticResources.editAddressHeading
                                    )}
                                    summary={addressSummary}
                                    isDeletable={true}
                                    badgeText={
                                        address?.preferred
                                            ? intl.formatMessage(staticResources.preferred)
                                            : undefined
                                    }
                                    onEdit={() => handleEditAddress(address)}
                                    onDelete={() => handleRemoveAddress(addressId)}
                                    isOpen={isEditingThisAddress}
                                    mb={4}
                                    key={addressId || index}
                                >
                                    {isEditingThisAddress ? (
                                        <AddressForm
                                            form={form}
                                            submitForm={submitForm}
                                            addressMode={addressMode}
                                            globalErrorRef={globalErrorRef}
                                            watchedValues={watchedValues}
                                        />
                                    ) : null}
                                </InfoCard>
                            )
                        })}
                    </Box>
                    <Button
                        onClick={handleAddAddress}
                        {...baseStyle.cta}
                        {...baseStyle.centerContent}
                        w={'50%'}
                        ml={`inherit`}
                    >
                        {intl.formatMessage(staticResources.addAddressLabel)}
                    </Button>
                </Flex>
            )}
            {(isLoading || isCustomerLoading) && <LoadingSpinner />}

            {/* Address Validation Drawer */}
            <AddressValidationDrawer
                isOpen={isAVSDrawerOpen}
                onClose={handleAVSDrawerSubmitOrClose}
                isZero={isZero}
                isSingle={isSingle}
                isPoxBoxOrMilitary={isPoxBoxOrMilitary}
                selectedAddress={selectedValidatedAddress}
                setSelectedAddress={setSelectedValidatedAddress}
                suggestions={addressSuggestions}
                enteredAddress={{
                    address1: form.getValues('address1'),
                    city: form.getValues('city'),
                    stateCode: form.getValues('stateCode'),
                    postalCode: form.getValues('postalCode')
                }}
            />
        </Stack>
    )
}

AddressBook.getTemplateName = () => 'account-address-book'

export default AddressBook
